import React from "react";
import { Play, Clock } from "lucide-react";

const episodes = [
  {
    n: "Episode 14",
    title: "Caring for a Loved One Without Losing Yourself",
    duration: "28 min",
    topic: "Caregiving",
    blurb:
      "Quiet, honest words for caregivers who feel invisible — and the small practices that keep your heart from running dry.",
  },
  {
    n: "Episode 13",
    title: "When Hope Feels Quieter Than Usual",
    duration: "22 min",
    topic: "Encouragement",
    blurb:
      "Seasons of quiet aren&apos;t failure. A pastoral look at staying grounded when everything inside you feels small.",
  },
  {
    n: "Episode 12",
    title: "When Grief Doesn&apos;t Have Words",
    duration: "24 min",
    topic: "Grief",
    blurb:
      "A chaplain&apos;s reflection on the kind of grief that hides behind a steady face — and gentle ways to begin again.",
  },
];

const platforms = [
  { name: "Spotify", id: "spotify", href: "#listen-spotify" },
  { name: "Apple Podcasts", id: "apple", href: "#listen-apple" },
  { name: "YouTube", id: "youtube", href: "#listen-youtube" },
  { name: "Amazon Music", id: "amazon", href: "#listen-amazon" },
];

const Podcast = () => {
  return (
    <section
      id="podcast"
      data-testid="podcast-section"
      className="py-20 lg:py-28 bg-[#F8FAFC]"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12">
          <div className="max-w-2xl">
            <span className="text-xs uppercase tracking-[0.22em] font-semibold text-[#D97706]">
              The Podcast
            </span>
            <h2 className="mt-4 font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tight text-slate-900 leading-tight">
              Conversations that meet you where you are.
            </h2>
            <p className="mt-4 text-slate-600 text-base sm:text-lg leading-relaxed">
              Honest, unhurried episodes on grief, caregiving, emotional
              stress, faith, workplace challenges, and the everyday questions
              people often carry quietly. Drawing on{" "}
              <em>From Diagnosis to Goodbye</em> and his experience as a
              hospice and corporate chaplain, Chaplain Derrick shares practical
              reflections for supporting people through difficult seasons at
              home and at work.
            </p>
          </div>

          <div className="flex flex-wrap gap-2">
            {platforms.map((p) => (
              <a
                key={p.id}
                href={p.href}
                data-testid={`podcast-platform-${p.id}`}
                className="text-xs sm:text-sm bg-white border border-slate-200 hover:border-[#0369A1] text-slate-700 rounded-full px-4 py-2 transition-colors"
              >
                Listen on {p.name}
              </a>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {episodes.map((ep, idx) => (
            <article
              key={idx}
              data-testid={`podcast-episode-${idx}`}
              className="bg-white rounded-2xl border border-slate-100 p-6 lift flex flex-col"
            >
              <div className="flex items-center justify-between text-xs">
                <span className="uppercase tracking-widest text-[#0369A1] font-semibold">
                  {ep.n}
                </span>
                <span className="inline-flex items-center gap-1 text-slate-500">
                  <Clock size={12} /> {ep.duration}
                </span>
              </div>
              <h3
                className="mt-4 font-heading text-xl text-slate-900 leading-snug"
                dangerouslySetInnerHTML={{ __html: ep.title }}
              />
              <span className="mt-3 inline-flex w-fit text-[11px] uppercase tracking-widest text-[#D97706] bg-amber-50 rounded-full px-2.5 py-1">
                {ep.topic}
              </span>
              <p
                className="mt-4 text-sm text-slate-600 leading-relaxed flex-1"
                dangerouslySetInnerHTML={{ __html: ep.blurb }}
              />
              <a
                href="#listen"
                data-testid={`podcast-listen-${idx}`}
                className="mt-6 inline-flex items-center gap-2 text-sm text-[#0369A1] hover:text-[#0284C7] font-medium"
              >
                <span className="inline-flex items-center justify-center h-9 w-9 rounded-full bg-[#0369A1] text-white">
                  <Play size={14} fill="white" />
                </span>
                Listen now
              </a>
            </article>
          ))}
        </div>

        <p className="mt-10 text-center text-sm text-slate-500">
          New episodes added regularly — gentle, practical, and never rushed.
        </p>
      </div>
    </section>
  );
};

export default Podcast;
